// ============================================================
// Stocora — Receiving helpers (Pilot Task 6, #3, 2026-06-17)
// ============================================================
// Data-side of the manual "mark as received" flow (Drew's
// Option A / manual-confirm). After Task 4 disabled email
// auto-credit, receiving is the SOLE path that advances
// order_items.qty_received and credits inventory.
//
//   - loadReceivableOrder: resolves an order + its line items
//     (with ordered vs already-received qty) for the receive UI.
//   - receiveOrder: calls the receive_order RPC with per-line
//     received quantities. The RPC clamps each line to its
//     remaining amount (idempotent), writes inventory_events
//     (kind='received') + order_shipment_items, and derives the
//     order status (received / partial_received).
//
// All operations take an explicit clinicId, passed as both a
// query filter (defense-in-depth) and to the RLS / RPC boundary
// (the actual security gate). Any staff may receive (per
// ratified UX 2026-05-28).
// ============================================================

import { supabase } from './supabase';
import type { Json } from './database.types';

// Typed client. `receive_order`, `orders`, and `order_items` are all in
// the generated database.types as of the 2026-06-29 regen, so RPC and
// table calls below are fully typed (no `as any` boundary cast).
const sb = supabase;

export interface ReceivableLine {
  order_item_id: string;
  product_id: string;
  product_name: string;
  uom: string;
  qty_ordered: number;
  qty_received: number;
  /** qty_ordered - qty_received, never negative. */
  qty_remaining: number;
}

export interface ReceivableOrder {
  order_id: string;
  vendor_name: string;
  status: string;
  lines: ReceivableLine[];
}

export type LoadReceivableResult =
  | { kind: 'ok'; order: ReceivableOrder }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

/**
 * Load an order and its line items for the receive screen.
 * Returns ordered + already-received quantities per line so the
 * UI can show remaining and pre-fill "received in full".
 */
export async function loadReceivableOrder(
  clinicId: string,
  orderId: string,
): Promise<LoadReceivableResult> {
  // Order + vendor name.
  const { data: orderRow, error: orderErr } = await sb
    .from('orders')
    .select('id, status, vendor_id, vendors(name)')
    .eq('clinic_id', clinicId)
    .eq('id', orderId)
    .maybeSingle();

  if (orderErr) {
    return { kind: 'error', message: orderErr.message };
  }
  if (!orderRow) {
    return { kind: 'not_found' };
  }

  // Line items + product name/uom.
  const { data: itemRows, error: itemErr } = await sb
    .from('order_items')
    .select('id, product_id, qty, qty_received, products(name, uom)')
    .eq('clinic_id', clinicId)
    .eq('order_id', orderId)
    .order('id', { ascending: true });

  if (itemErr) {
    return { kind: 'error', message: itemErr.message };
  }

  interface RawItemRow {
    id: string;
    product_id: string;
    qty: number;
    qty_received: number | null;
    products: { name: string; uom: string } | null;
  }

  const lines: ReceivableLine[] = ((itemRows ?? []) as RawItemRow[]).map((r) => {
    const ordered = r.qty;
    const received = r.qty_received ?? 0;
    return {
      order_item_id: r.id,
      product_id: r.product_id,
      product_name: r.products?.name ?? '(unknown product)',
      uom: r.products?.uom ?? '',
      qty_ordered: ordered,
      qty_received: received,
      qty_remaining: Math.max(0, ordered - received),
    };
  });

  return {
    kind: 'ok',
    order: {
      order_id: orderRow.id as string,
      vendor_name: orderRow.vendors?.name ?? '(unknown vendor)',
      status: orderRow.status as string,
      lines,
    },
  };
}

export interface ReceiveLineInput {
  order_item_id: string;
  qty: number;
}

export interface ReceiveLineResult {
  order_item_id: string;
  credited: number;
  reason: string;
}

export interface ReceiveResult {
  order_id: string;
  shipment_id: string | null;
  lines_credited: number;
  units_credited: number;
  order_status: string;
  lines: ReceiveLineResult[];
}

export type ReceiveOrderOutcome =
  | { kind: 'ok'; result: ReceiveResult }
  | { kind: 'error'; message: string };

/**
 * Call the receive_order RPC. The RPC clamps each line to its
 * remaining quantity, so passing the full ordered amount for a
 * "received in full" tap is safe and idempotent — re-submitting
 * credits nothing further.
 *
 * actorId is the authenticated receiver; it lands on the
 * inventory_events.actor_id audit trail.
 */
export async function receiveOrder(
  clinicId: string,
  orderId: string,
  lines: ReceiveLineInput[],
  actorId: string,
): Promise<ReceiveOrderOutcome> {
  const { data, error } = await sb.rpc('receive_order', {
    p_order_id: orderId,
    p_clinic_id: clinicId,
    // p_lines is a jsonb param → typed as Json by the generated types.
    // ReceiveLineInput[] is JSON-serializable but lacks the index
    // signature Json requires, so cast at this one serialization seam.
    p_lines: lines as unknown as Json,
    p_actor_id: actorId,
  });

  if (error) {
    return { kind: 'error', message: error.message };
  }
  // receive_order returns jsonb → typed as Json; the RPC's actual shape is
  // ReceiveResult (see the migration's RETURNS). Narrow through unknown.
  return { kind: 'ok', result: data as unknown as ReceiveResult };
}
